import type { CSSProperties, FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { formatDate } from '@/lib/format'
import { useInView } from '@/hooks/use-in-view'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { FigLabel } from '@/components/generals/fig-label'
import { PageHeader } from '@/components/generals/page-header'
import { Reveal } from '@/components/generals/reveal'
import { ColumnChart } from '@/components/charts/column-chart'

import {
    AWARDS,
    MEDIA,
    PRESS,
    PUBLICATIONS
} from '@/constants/recognition'

const delay = (value: number): CSSProperties => ({ '--delay': `${value}ms` } as CSSProperties)

const PublicationsChart: FC = (): ReactNode => {
    const [ref, inView] = useInView<HTMLDivElement>({ rootMargin: '0px 0px -15% 0px' })

    return (
        <div ref={ref} data-reveal={inView ? 'in' : 'pre'}>
            <ColumnChart
                title='Projects published per year, 2017 to 2025'
                labels={PUBLICATIONS.labels}
                values={PUBLICATIONS.values}
                unit='projects published'
                height={260}
            />
        </div>
    )
}

const RecognitionPage: FC = (): ReactNode => {
    const published = PUBLICATIONS.values.reduce((sum, value) => sum + value, 0)

    return (
        <Layout>
            <Seo
                title='Recognition'
                description='Awards, press and publications for Norrvik: the Nordic Timber Prize, the Iberian Housing Prize and a Baltic Culture shortlist, with press coverage and a media list.'
                figure='Fig. 01 / Recognition / Awards and press'
                shape='hall'
            />

            <PageHeader
                fig='01'
                title='Recognition'
                detail='Awards and press'
                note='2017 to 2026'
                lines={['Noted,', 'measured,', 'cited.']}
                intro={(
                    <p>
                        Prizes matter to us less than the readings that follow, but they bring the work to people who commission buildings. Below are the awards, the press and the projects published each year.
                    </p>
                )}
                readouts={[
                    { label: 'Awards', value: String(AWARDS.filter(award => award.result === 'Winner').length).padStart(2, '0') },
                    { label: 'Shortlists', value: String(AWARDS.filter(award => award.result !== 'Winner').length).padStart(2, '0') },
                    { label: 'Publications', value: published },
                    { label: 'Since', value: PUBLICATIONS.labels[0] }
                ]}
            />

            <section aria-labelledby='awards-title' className='pt-20 desk:pt-28'>
                <Reveal className='px-gutter pb-10'>
                    <FigLabel fig='02' title='Awards' detail='Prizes and shortlists' note='Newest first' />

                    <h2 id='awards-title' className='sr-only'>
                        Awards
                    </h2>
                </Reveal>

                <Reveal>
                    <table className='w-full border-collapse text-left'>
                        <caption className='sr-only'>
                            Awards and shortlists with year, prize, result and project
                        </caption>

                        <thead className='hidden desk:table-header-group'>
                            <tr className='border-y border-hairline'>
                                {['Year', 'Prize', 'Result', 'Project', 'Citation'].map((heading, index) => (
                                    <th key={heading} scope='col' className={index === 0 ? 'hud py-4 pl-gutter pr-6 font-normal text-mute' : index === 4 ? 'hud py-4 pr-gutter font-normal text-mute' : 'hud py-4 pr-6 font-normal text-mute'}>
                                        {heading}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody className='border-t border-hairline desk:border-t-0'>
                            {AWARDS.map((award, index) => (
                                <tr key={award.name} className='reveal-fade grid border-b border-hairline px-gutter py-6 desk:table-row desk:px-0 desk:py-0' style={delay(index * 90)}>
                                    <td className='hud text-mute desk:py-7 desk:pl-gutter desk:pr-6 desk:align-top'>
                                        {award.year}
                                    </td>

                                    <td className='mt-2 font-heading text-[clamp(24px,2.4vw,34px)] font-medium uppercase leading-none tracking-[-0.02em] text-ink desk:mt-0 desk:py-7 desk:pr-6 desk:align-top'>
                                        {award.name}
                                    </td>

                                    <td className='hud mt-3 desk:mt-0 desk:py-7 desk:pr-6 desk:align-top'>
                                        <span className='inline-flex items-center gap-2'>
                                            <span className={award.result === 'Winner' ? 'size-1.5 bg-signal' : 'size-1.5 border border-mute'} aria-hidden='true' />
                                            <span className='text-ink'>
                                                {award.result}
                                            </span>
                                        </span>
                                    </td>

                                    <td className='mt-3 desk:mt-0 desk:py-7 desk:pr-6 desk:align-top'>
                                        <Link href={award.href} className='hud inline-flex items-center gap-2 text-ink underline decoration-hairline underline-offset-4 hover:decoration-ink'>
                                            {award.project}
                                            <ArrowUpRight className='size-4' strokeWidth={1.5} aria-hidden='true' />
                                        </Link>
                                    </td>

                                    <td className='mt-3 max-w-[48ch] text-[14px] leading-[1.6] text-ink/75 desk:mt-0 desk:py-7 desk:pr-gutter desk:align-top'>
                                        {award.note}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Reveal>
            </section>

            <section aria-labelledby='press-title' className='pt-20 desk:pt-28'>
                <Reveal className='px-gutter pb-10'>
                    <FigLabel fig='03' title='Press' detail='Coverage' note='Outlet / date / headline' />

                    <h2 id='press-title' className='display-lg mt-10'>
                        <span className='reveal-line'>
                            <span className='reveal-inner text-ink' style={delay(80)}>
                                What others
                            </span>
                        </span>

                        <span className='reveal-line'>
                            <span className='reveal-inner text-dim' style={delay(170)}>
                                wrote down.
                            </span>
                        </span>
                    </h2>
                </Reveal>

                <ul className='border-t border-hairline'>
                    {PRESS.map(item => (
                        <li key={`${item.outlet}-${item.date}`} className='group relative border-b border-hairline'>
                            <span className='absolute inset-y-0 left-0 w-[3px] origin-top scale-y-0 bg-signal transition-transform duration-500 ease-instrument group-focus-within:scale-y-100 group-hover:scale-y-100' aria-hidden='true' />

                            <Link href={item.href} className='grid gap-x-8 gap-y-2 px-gutter py-7 transition-colors duration-300 hover:bg-panel desk:grid-cols-[14rem_9rem_minmax(0,1fr)_auto] desk:items-baseline'>
                                <span className='hud text-ink'>
                                    {item.outlet}
                                </span>

                                <span className='hud text-mute'>
                                    <time dateTime={item.date}>
                                        {formatDate(item.date)}
                                    </time>
                                </span>

                                <span className='font-heading text-[clamp(20px,2vw,28px)] font-medium leading-[1.15] tracking-[-0.015em] text-ink transition-transform duration-500 ease-instrument desk:group-hover:translate-x-2'>
                                    {item.headline}
                                </span>

                                <ArrowUpRight className='hidden size-5 text-ink transition-transform duration-500 ease-instrument group-hover:-translate-y-1 group-hover:translate-x-1 desk:block' strokeWidth={1.25} aria-hidden='true' />
                            </Link>
                        </li>
                    ))}
                </ul>
            </section>

            <section aria-labelledby='published-title' className='pt-20 desk:pt-28'>
                <Reveal className='grid gap-12 px-gutter desk:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] desk:gap-16'>
                    <div>
                        <FigLabel fig='04' title='Publications' detail='Projects published per year' note={`${published} publications / 2017 to 2025`} />

                        <h2 id='published-title' className='display-lg mt-10'>
                            <span className='reveal-line'>
                                <span className='reveal-inner text-ink' style={delay(80)}>
                                    Projects published
                                </span>
                            </span>

                            <span className='reveal-line'>
                                <span className='reveal-inner text-dim' style={delay(170)}>
                                    per year.
                                </span>
                            </span>
                        </h2>
                    </div>

                    <div className='reveal-fade' style={delay(200)}>
                        <PublicationsChart />
                    </div>
                </Reveal>
            </section>

            <section aria-labelledby='media-title' className='pb-24 pt-20 desk:pb-32 desk:pt-28'>
                <Reveal className='px-gutter pb-10'>
                    <FigLabel fig='05' title='Media' detail='Press kit and project sheets' note='Item / contents / format' />

                    <h2 id='media-title' className='sr-only'>
                        Media list
                    </h2>
                </Reveal>

                <ul className='border-t border-hairline'>
                    {MEDIA.map((item, index) => {
                        const external = item.href.startsWith('mailto:')
                        const content = (
                            <>
                                <span className='hud text-mute'>
                                    {String(index + 1).padStart(2, '0')}
                                </span>

                                <span className='hud text-ink'>
                                    {item.label}
                                </span>

                                <span className='hud text-mute'>
                                    {item.detail}
                                </span>

                                <span className='hud flex items-center gap-2 text-ink desk:justify-end'>
                                    {item.format}
                                    <ArrowUpRight className='size-4' strokeWidth={1.5} aria-hidden='true' />
                                </span>
                            </>
                        )
                        const className = 'grid gap-x-8 gap-y-1 px-gutter py-4 transition-colors duration-300 hover:bg-panel desk:grid-cols-[3rem_minmax(0,1.2fr)_minmax(0,1.4fr)_9rem] desk:items-baseline'

                        return (
                            <li key={item.label} className='border-b border-hairline'>
                                {external ? (
                                    <a href={item.href} className={className}>
                                        {content}
                                    </a>
                                ) : (
                                    <Link href={item.href} className={className}>
                                        {content}
                                    </Link>
                                )}
                            </li>
                        )
                    })}
                </ul>
            </section>
        </Layout>
    )
}

export default RecognitionPage
